import * as React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, CheckCircle2, Sparkles } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { api, ApiError } from '../api-client';
import { GoogleSignInButton } from './GoogleSignInButton';
import { MfaChallengeStep } from './MfaChallengeStep';
import { WorqlyMark } from '../../design-system/components/WorqlyMark';
import { Sparkline, WaveStockChart } from '../../design-system/charts';
import { appUrl, getHostMode } from '../hostMode';
import { TurnstileWidget } from '../../marketing/components/TurnstileWidget';

export function LoginPage() {
  const { login, chooseWorkspace, logout, adoptToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const [widgetEpoch, setWidgetEpoch] = React.useState(0);

  // One picker serves both routes into it: Google (an identity that is a
  // member of several workspaces) and password (an email that is unique only
  // per tenant, so the same address + password can authenticate in more than
  // one). `source` decides which endpoint completes the choice.
  interface PickerWorkspace { tenantId: string; tenantName: string }
  interface WorkspacePicker {
    source: 'google' | 'password';
    pendingToken: string;
    workspaces: PickerWorkspace[];
  }
  const [picker, setPicker] = React.useState<WorkspacePicker | null>(null);
  const [noAccountEmail, setNoAccountEmail] = React.useState<string | null>(null);
  const [pickerBusy, setPickerBusy] = React.useState(false);
  const [mfaToken, setMfaToken] = React.useState<string | null>(null);

  function goNext() {
    const next = (location.state as any)?.from ?? '/dashboard';
    if (getHostMode() === 'marketing') {
      window.location.replace(appUrl(next));
    } else {
      navigate(next, { replace: true });
    }
  }

  function oauthError(err: unknown) {
    if (err instanceof ApiError) {
      if (err.code === 'rate_limited') setError('Too many attempts from your IP — please wait a few minutes.');
      else setError(err.message);
    } else {
      setError(String((err as any)?.message ?? err));
    }
  }

  /** Shared "not the customer app" bail-out used by every success branch. */
  function rejectNonUser() {
    logout({ redirectTo: null });
    setError('This is the customer sign-in. Operator accounts use the admin portal.');
  }

  async function onGoogleCredential(credential: string) {
    setError(null);
    setNoAccountEmail(null);
    setPickerBusy(true);
    try {
      const r = await api<any>('/api/auth/oauth/google', {
        method: 'POST', body: { credential }, noRedirectOn401: true,
      });
      if (r.status === 'ok') {
        await adoptToken(r.token);
        goNext();
      } else if (r.status === 'mfa_required') {
        setMfaToken(r.mfa_token);
      } else if (r.status === 'choose_workspace') {
        setPicker({ source: 'google', pendingToken: r.pendingToken, workspaces: r.workspaces });
      } else if (r.status === 'no_account') {
        setNoAccountEmail(r.email);
      }
    } catch (err) {
      oauthError(err);
    } finally {
      setPickerBusy(false);
    }
  }

  async function onChooseWorkspace(tenantId: string) {
    if (!picker) return;
    const { source, pendingToken } = picker;
    setError(null);
    setPickerBusy(true);
    try {
      if (source === 'password') {
        // Second step of an ambiguous password login. The picker is NOT a
        // second factor and must never stand in for one — a TOTP account
        // still comes back as mfa_required here.
        const r = await chooseWorkspace({ pendingToken, tenantId });
        if ('mfa_required' in r) {
          setPicker(null);
          setMfaToken(r.mfa_token);
          return;
        }
        if (r.type !== 'user') {
          setPicker(null);
          rejectNonUser();
          return;
        }
        goNext();
        return;
      }
      const r = await api<any>('/api/auth/oauth/google/complete', {
        method: 'POST', body: { pendingToken, tenantId }, noRedirectOn401: true,
      });
      if (r.status === 'mfa_required') {
        setPicker(null);
        setMfaToken(r.mfa_token);
        return;
      }
      await adoptToken(r.token);
      goNext();
    } catch (err) {
      setPicker(null);
      oauthError(err);
    } finally {
      setPickerBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login({ email, password, turnstileToken, type: 'user' });
      if ('mfa_required' in result) {
        setMfaToken(result.mfa_token);
        return;
      }
      if ('workspace_selection_required' in result) {
        // This email + password is valid in more than one workspace. No
        // session was issued — the user picks, then we finish the login.
        setPicker({ source: 'password', pendingToken: result.pendingToken, workspaces: result.workspaces });
        return;
      }
      if ('mfa_enrollment_required' in result) {
        // Tenant-user logins never hit the admin MFA-enforcement gate — this
        // is a defensive narrow only, mirroring the `type !== 'user'` guard.
        rejectNonUser();
        return;
      }
      if (result.type !== 'user') {
        rejectNonUser();
        return;
      }
      goNext();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'captcha_failed') {
          setError(`Captcha: ${err.message || 'verification failed'} — please try again.`);
          setTurnstileToken('');
          setWidgetEpoch((n) => n + 1);
        } else if (err.code === 'rate_limited') {
          setError('Too many login attempts from your IP — please wait a few minutes and try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError(String((err as any)?.message ?? err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen flex">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-pds-mesh opacity-95" />

      {/* Form side */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="relative z-10 px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="group" aria-label="Worqly home">
            <WorqlyMark size="md" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink/65 hover:text-ink dark:text-white/70 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to worqly.io
          </Link>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md">
            <p className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Sign in</p>
            <h1 className="font-light text-display-md text-ink dark:text-white tracking-[-0.025em] leading-[1.05]">
              Welcome back.
            </h1>
            <p className="text-[14.5px] text-ink/60 dark:text-white/60 mt-3 mb-8">
              Sign in to send invoices, track payments, and run your team.
            </p>

            <div className="rounded-3xl bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card p-7">
              {mfaToken ? (
                <MfaChallengeStep
                  mfaToken={mfaToken}
                  onBack={() => { setMfaToken(null); setError(null); }}
                  onSuccess={(identity) => {
                    if (identity.type !== 'user') {
                      rejectNonUser();
                      setMfaToken(null);
                      return;
                    }
                    goNext();
                  }}
                />
              ) : picker ? (
                <div className="space-y-3">
                  <p className="text-[13.5px] font-semibold text-ink/80 dark:text-white/80">
                    Choose your workspace
                  </p>
                  <p className="text-[12.5px] text-ink/55 dark:text-white/55">
                    {picker.source === 'google'
                      ? 'Your Google account is a member of several workspaces.'
                      : 'This email is used in more than one workspace. Pick the one to sign in to.'}
                  </p>
                  {picker.workspaces.map((w) => (
                    <button
                      key={w.tenantId}
                      type="button"
                      disabled={pickerBusy}
                      onClick={() => onChooseWorkspace(w.tenantId)}
                      className="w-full text-left rounded-2xl px-4 py-3 ring-1 ring-ink/10 dark:ring-white/15 hover:ring-coral hover:bg-coral/5 transition-colors text-[14px] font-semibold text-ink dark:text-white"
                    >
                      {w.tenantName}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="text-[12.5px] font-semibold text-ink/50 dark:text-white/50 hover:underline"
                    onClick={() => { setPicker(null); setError(null); }}
                  >
                    ← Back to sign in
                  </button>
                  {error && (
                    <div className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2" role="alert">
                      {error}
                    </div>
                  )}
                </div>
              ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[12px] font-semibold text-ink/70 dark:text-white/70">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-[12px] font-semibold text-ink/70 dark:text-white/70">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-[12px] font-semibold text-ink/50 hover:text-ink dark:text-white/50 dark:hover:text-white transition-colors underline-offset-2 hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <TurnstileWidget
                  key={widgetEpoch}
                  onToken={setTurnstileToken}
                  onExpire={() => setTurnstileToken('')}
                />
                {error && (
                  <div className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2" role="alert">
                    {error}
                  </div>
                )}
                <Button type="submit" size="lg" className="w-full" disabled={submitting || !turnstileToken}>
                  {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
                <div className="flex items-center gap-3 pt-1" aria-hidden>
                  <div className="h-px flex-1 bg-ink/10 dark:bg-white/15" />
                  <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-ink/40 dark:text-white/40">or</span>
                  <div className="h-px flex-1 bg-ink/10 dark:bg-white/15" />
                </div>
                <GoogleSignInButton text="signin_with" onCredential={onGoogleCredential} />
                {noAccountEmail && (
                  <p className="text-[13px] text-ink/65 dark:text-white/65 bg-amber-500/10 ring-1 ring-amber-500/20 rounded-2xl px-3 py-2">
                    No workspace found for <span className="font-bold">{noAccountEmail}</span>.{' '}
                    <Link to="/signup" className="font-bold text-coral underline-offset-2 hover:underline">
                      Start a free trial instead?
                    </Link>
                  </p>
                )}
              </form>
              )}
            </div>

            <p className="text-center text-[13px] text-ink/60 dark:text-white/60 mt-8">
              New to Worqly?{' '}
              <Link
                to="/signup"
                className="inline-flex items-center gap-1 font-bold text-ink dark:text-white hover:text-coral dark:hover:text-coral transition-colors"
              >
                Start your free trial
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </p>
          </div>
        </main>
      </div>

      {/* Marketing side */}
      <aside className="hidden lg:flex w-[520px] xl:w-[640px] shrink-0 relative items-center justify-center overflow-hidden">
        <div className="absolute inset-y-4 right-4 left-0 rounded-l-[40px] bg-coral-gradient" />
        <div className="absolute inset-y-4 right-4 left-0 rounded-l-[40px] pds-dotted opacity-25 mix-blend-overlay" />
        <div className="relative z-10 px-12 max-w-md text-white">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 backdrop-blur-md rounded-full text-[11px] font-bold uppercase tracking-[0.10em] mb-6">
            <Sparkles className="h-3 w-3" /> Worqly · Pearl + Coral
          </span>
          <h2 className="text-display-md font-light leading-tight tracking-[-0.025em] mb-6">
            Sell, bill, and run your team — beautifully.
          </h2>
          <ul className="space-y-3 text-[14px]">
            {[
              'PDF invoices with branded pay links',
              'Stripe + manual payments reconciled',
              'HR, payroll, time tracking, projects — in one place',
              'Free until you send your first invoice',
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          {/* Mock chart */}
          <div className="mt-10 rounded-3xl bg-white/10 backdrop-blur-md ring-1 ring-white/20 p-5">
            <p className="text-[11px] uppercase tracking-[0.16em] font-bold opacity-80 mb-2">Revenue · last 12 weeks</p>
            <p className="text-stat-lg tabular-nums font-bold">$182,409</p>
            <p className="text-[12px] opacity-75 mt-1">+24.6% vs. last quarter</p>
            <div className="mt-3">
              <WaveStockChart data={[20, 35, 28, 42, 38, 55, 48, 62, 58, 70, 65, 80]} color="white" filled height={80} />
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
